import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, parseISO, isPast } from 'date-fns';
import { 
  ArrowLeft, Calendar, Loader2, MapPin, Clock, 
  AlertTriangle, CheckCircle2, Circle, ChevronRight,
  Check, X, Timer, Trash2
} from 'lucide-react';
import { getSlotLabel, getSlotStyles } from '../utils/slotLabels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HoldSlotDisplay = ({ slot, hold, eventId, selectable, selected, onToggle }) => {
  const slotStyles = getSlotStyles(slot);
  
  const isEmpty = !hold || hold.status === 'Empty';
  const isActive = hold?.status === 'Active';
  const isExpired = hold?.status === 'Expired';
  const isReleased = hold?.status === 'Released';

  const getTimeRemaining = () => {
    if (!isActive || !hold?.until) return null;
    try {
      const untilDate = parseISO(hold.until);
      if (isPast(untilDate)) return 'Expired';
      const now = new Date();
      const diff = untilDate - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `${days}d ${hours % 24}h`;
      }
      return `${hours}h ${minutes}m`;
    } catch {
      return null;
    }
  };

  const timeRemaining = getTimeRemaining();

  return (
    <div 
      className={`p-3 rounded-lg border-l-4 flex items-start gap-3 ${
        isActive ? `${slotStyles.border} bg-card` :
        isEmpty ? 'border-l-gray-300 dark:border-l-gray-700 bg-muted/30' :
        isExpired ? 'border-l-red-400 bg-red-50/50 dark:bg-red-900/10' :
        isReleased ? 'border-l-green-400 bg-green-50/50 dark:bg-green-900/10' :
        'border-l-gray-300 bg-muted/30'
      }`}
    >
      {selectable && isActive && (
        <div className="pt-0.5">
          <Checkbox
            checked={selected}
            onCheckedChange={onToggle}
            data-testid={`select-hold-${eventId}-${slot}`}
          />
        </div>
      )}
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold ${slotStyles.text}`}>
              {getSlotLabel(slot)}
            </span>
            {isEmpty && <Badge variant="outline" className="text-xs">Empty</Badge>}
            {isActive && (
              <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Active</Badge>
            )}
            {isExpired && <Badge variant="outline" className="text-xs text-red-600 border-red-300">Expired</Badge>}
            {isReleased && <Badge variant="outline" className="text-xs text-green-600 border-green-300">Released</Badge>}
          </div>
          {isActive && timeRemaining && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />{timeRemaining}
            </span>
          )}
        </div>
        {!isEmpty && (
          <div className="mt-2 text-sm">
            {hold.day && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>{hold.day}</span>
                {hold.time && <span>at {hold.time}</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EventHoldCard = ({ event, selectedHolds, onToggleHold, bulkMode }) => {
  const navigate = useNavigate();
  
  const activeHoldsCount = [event.hold1, event.hold2, event.hold3].filter(
    h => h?.status === 'Active'
  ).length;

  return (
    <Card className="hover:shadow-md transition-all" data-testid={`event-holds-card-${event.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{event.title}</CardTitle>
            {(event.city || event.state) && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <MapPin className="h-3 w-3" />
                {event.city}{event.city && event.state ? ', ' : ''}{event.state}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeHoldsCount > 0 && (
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                {activeHoldsCount} Active
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate(`/events/${event.id}`)} data-testid={`view-event-${event.id}`}>
              View Event <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {[1, 2, 3].map(slot => {
            const hold = event[`hold${slot}`];
            const key = `${event.id}:${slot}`;
            return (
              <HoldSlotDisplay
                key={slot}
                slot={slot}
                hold={hold}
                eventId={event.id}
                selectable={bulkMode}
                selected={selectedHolds.has(key)}
                onToggle={() => onToggleHold(event.id, slot)}
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const MyHolds = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedHolds, setSelectedHolds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`, { headers: getAuthHeaders() });
      const draftEvents = response.data.filter(e => e.status === 'Draft');
      setEvents(draftEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const toggleHold = (eventId, slot) => {
    const key = `${eventId}:${slot}`;
    setSelectedHolds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllActive = () => {
    const all = new Set();
    events.forEach(event => {
      [1, 2, 3].forEach(slot => {
        const hold = event[`hold${slot}`];
        if (hold?.status === 'Active') all.add(`${event.id}:${slot}`);
      });
    });
    setSelectedHolds(all);
  };

  const clearSelection = () => setSelectedHolds(new Set());

  const executeBulkAction = async (action) => {
    if (selectedHolds.size === 0) return;
    setBulkLoading(true);
    try {
      const holds = Array.from(selectedHolds).map(key => {
        const [eventId, slot] = key.split(':');
        return { eventId, slot: parseInt(slot) };
      });
      const res = await axios.post(`${API}/holds/bulk-action`, 
        { holds, action, extendDays: 7 },
        { headers: getAuthHeaders() }
      );
      const data = res.data;
      if (data.success > 0) {
        toast.success(`${action === 'release' ? 'Released' : 'Extended'} ${data.success} hold${data.success !== 1 ? 's' : ''}`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} hold${data.failed !== 1 ? 's' : ''} failed`);
      }
      setSelectedHolds(new Set());
      setBulkMode(false);
      await fetchEvents();
    } catch (error) {
      toast.error('Bulk action failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredEvents = events.filter(event => {
    if (filter === 'all') return true;
    const hasActive = [event.hold1, event.hold2, event.hold3].some(h => h?.status === 'Active');
    const allEmpty = [event.hold1, event.hold2, event.hold3].every(h => !h || h.status === 'Empty');
    if (filter === 'active') return hasActive;
    if (filter === 'empty') return allEmpty;
    return true;
  });

  const sortedEvents = [...filteredEvents].sort((a, b) =>
    new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
  );

  const totalActiveHolds = events.reduce((count, event) =>
    count + [event.hold1, event.hold2, event.hold3].filter(h => h?.status === 'Active').length, 0
  );

  const eventsWithActiveHolds = events.filter(event =>
    [event.hold1, event.hold2, event.hold3].some(h => h?.status === 'Active')
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="my-holds-page">
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/hub')} className="h-8 w-8" data-testid="back-to-hub-btn">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  My Event Holds
                </h1>
                <p className="text-xs text-muted-foreground">
                  {events.length} event{events.length !== 1 ? 's' : ''} &bull; {totalActiveHolds} active hold{totalActiveHolds !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            {totalActiveHolds > 0 && (
              <Button
                variant={bulkMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setBulkMode(!bulkMode); if (bulkMode) clearSelection(); }}
                data-testid="toggle-bulk-mode"
              >
                {bulkMode ? 'Cancel' : 'Bulk Actions'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {bulkMode && (
        <div className="sticky top-[65px] z-10 bg-primary/5 dark:bg-primary/10 border-b border-primary/20 backdrop-blur-sm" data-testid="bulk-action-bar">
          <div className="max-w-3xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground">
                {selectedHolds.size} selected
              </span>
              <Button variant="ghost" size="sm" className="text-xs" onClick={selectAllActive} data-testid="select-all-holds">
                Select All Active
              </Button>
              {selectedHolds.size > 0 && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={clearSelection} data-testid="clear-selection">
                  Clear
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                disabled={selectedHolds.size === 0 || bulkLoading}
                onClick={() => executeBulkAction('extend')}
                data-testid="bulk-extend-btn"
              >
                <Timer className="h-3.5 w-3.5 mr-1" />
                Extend +7d
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="text-xs"
                disabled={selectedHolds.size === 0 || bulkLoading}
                onClick={() => executeBulkAction('release')}
                data-testid="bulk-release-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Release
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 py-4">
        {events.length > 0 && (
          <div className="flex gap-2 mb-4">
            <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')} data-testid="filter-all">
              All ({events.length})
            </Button>
            <Button variant={filter === 'active' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('active')} data-testid="filter-active">
              With Active Holds ({eventsWithActiveHolds})
            </Button>
            <Button variant={filter === 'empty' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('empty')} data-testid="filter-empty">
              No Active Holds ({events.length - eventsWithActiveHolds})
            </Button>
          </div>
        )}

        {sortedEvents.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">
                {filter === 'all' ? 'No events with holds yet' : 'No matching events'}
              </h3>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                {filter === 'all' 
                  ? 'Create an event and add holds to see them here.'
                  : 'Try changing the filter to see more events.'}
              </p>
              {filter === 'all' && (
                <Button className="mt-4" onClick={() => navigate('/hub')} data-testid="create-event-btn">
                  Create Event
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sortedEvents.map(event => (
              <EventHoldCard
                key={event.id}
                event={event}
                selectedHolds={selectedHolds}
                onToggleHold={toggleHold}
                bulkMode={bulkMode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyHolds;
